import {
  Auth0Provider,
  useAuth0,
  withAuthenticationRequired,
} from "@auth0/auth0-react";
import React, { useEffect, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
  useParams,
} from "react-router-dom";
import { LoadingNewDocs } from "src/components/Assistant/LoadingNewDocs";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { AdminRoadmapCards } from "src/pages/AdminRoadmapCards";
import { AdminTaskDefinitions } from "src/pages/AdminTaskDefinitions";
import { Assistant } from "src/pages/assistant";
import { DU_Configure } from "src/pages/assistant/DocumentUpdate/DU_Configure";
import { DU_ListOfDocsToChange } from "src/pages/assistant/DocumentUpdate/DU_ListOfDocsToChange";
import { DU_MultiStepForm } from "src/pages/assistant/DocumentUpdate/DU_MultiStepForm";
import { DU_UpdateDocs } from "src/pages/assistant/DocumentUpdate/DU_UpdateDocs";
import { ChangeManagementPlanPage } from "src/pages/ChangeManagementPlan";
import { ChangeManagementPlansPage } from "src/pages/ChangeManagementPlans";
import { DeactivatedUserError } from "src/pages/DeactivatedUserError";
import { DocumentsPage } from "src/pages/Documents";
import { FrameViewerPage } from "src/pages/FrameViewer";
import { NavigateToHomeOrReturnTo } from "src/pages/NavigateToHomeOrReturnTo";
import { QualityEventRecordPage } from "src/pages/QualityEventRecord";
import { QualityEvents } from "src/pages/QualityEvents";
import { TrainingAssignment } from "src/pages/TrainingAssignment";
import { TrainingDetail } from "src/pages/TrainingDetail";
import { TrainingEdit } from "src/pages/TrainingEdit";
import { TrainingPrograms } from "src/pages/TrainingPrograms";
import { TrainingQuizEdit } from "src/pages/TrainingQuizEdit";
import { TrashDocumentViewerPage } from "src/pages/TrashDocumentViewer";
import { UserSettings } from "src/pages/UserSettings";
import { authHelper } from "src/stores/helpers";
import { fillRouteWithSlugs } from "src/utils";
import { MainLayout, MultiStepForm } from "../components";
import config from "../config";
import {
  AuthPage,
  CheckoutSuccess,
  ErrorPage,
  HelpCenter,
  HelpCenterArticle,
  Loading,
  Organization,
  OrganizationDashboard,
  Roadmap,
  Skills,
  TerminalPage,
} from "../pages";
import DeviceTasks from "../pages/DeviceTasks";
import { OAuthCallback } from "../pages/OAuthCallback";
import PinReset from "../pages/PinReset";
import QMS from "../pages/QMS";
import TaskDetail from "../pages/TaskDetail";
import Tasks from "../pages/Tasks";
import TraceabilityMatrix from "../pages/TraceabilityMatrix";

type ProtectedRouteProps = {
  component: any;
  [key: string]: any;
};

export const ProtectedRoute = ({ component, ...args }: ProtectedRouteProps) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
    logout,
    user,
  } = useAuth0();
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    // Update Auth0 functions in auth helper when they change
    authHelper.setAuth0Functions({
      loginWithRedirect,
      logout,
      user,
      isAuthenticated,
      getAccessTokenSilently,
    });

    setTokenReady(true);
  }, [loginWithRedirect, logout, user, isAuthenticated]);

  const Component = withAuthenticationRequired(component, {
    ...args,
    onRedirecting: () => <Loading />,
  });

  // Show loading until both authentication and token caching are complete
  if (isAuthenticated && !tokenReady) {
    return <Loading />;
  }

  return <Component />;
};

export const LegacyDeviceManagementRedirect = () => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const location = useLocation();

  return (
    <Navigate
      replace
      to={`${fillRouteWithSlugs(ROUTES.MANAGE_ORGANIZATION, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
      })}${location.search}`}
    />
  );
};

export const OrganizationIndexRedirect = () => {
  const { orgId = "" } = useParams<{ orgId: string }>();

  return (
    <Navigate
      replace
      to={fillRouteWithSlugs(ROUTES.DASHBOARD, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
      })}
    />
  );
};

export const GENERAL_ROUTES: RouteObject[] = [
  {
    path: ROUTES.MANAGE_ORGANIZATION,
    element: <Organization />,
  },
  {
    path: "",
    element: <OrganizationIndexRedirect />,
  },
  {
    path: ROUTES.DASHBOARD,
    element: <OrganizationDashboard />,
  },
  {
    path: ROUTES.DEVICE_MANAGEMENT,
    element: <LegacyDeviceManagementRedirect />,
  },
  {
    path: ROUTES.DOCUMENTS,
    element: <DocumentsPage />,
  },
  {
    path: ROUTES.CHANGE_MANAGEMENT_PLANS,
    element: <ChangeManagementPlansPage />,
  },
  {
    path: ROUTES.CHANGE_MANAGEMENT_PLAN,
    element: <ChangeManagementPlanPage />,
  },
  {
    path: ROUTES.TRASH_DOCUMENT,
    element: <TrashDocumentViewerPage />,
  },
  {
    path: ROUTES.FRAME_VIEWER,
    element: <FrameViewerPage />,
  },
  {
    path: ROUTES.TASKS,
    element: <Tasks />,
  },
  {
    path: ROUTES.SKILLS,
    element: <Skills />,
  },
  {
    path: ROUTES.ORG_TASK_DETAIL,
    element: <TaskDetail />,
  },
  {
    path: ROUTES.DEVICE_TASKS,
    element: <DeviceTasks />,
  },
  {
    path: ROUTES.TASK_DETAIL,
    element: <TaskDetail />,
  },
  {
    path: ROUTES.DEVICE_OVERVIEW,
    element: <Roadmap />,
  },
  { path: ROUTES.ORGANIZATION_DOCUMENT, element: <QMS /> },
  { path: ROUTES.QMS, element: <QMS /> },
  { path: ROUTES.QMS_OPEN_DOC, element: <QMS /> },
  { path: ROUTES.TRACEABILITY_MATRIX, element: <TraceabilityMatrix /> },
  { path: ROUTES.TEMPLATE_ASSISTANT, element: <MultiStepForm /> },
  { path: ROUTES.DOCUMENT_ASSISTANT, element: <MultiStepForm /> },
  {
    path: ROUTES.CHECKOUT_SUCCESS,
    element: <CheckoutSuccess />,
  },
  {
    path: ROUTES.ASSISTANT,
    element: <Assistant />,
  },
  {
    path: ROUTES.ASSISTANT_DU_CONFIGURE,
    element: <DU_Configure />,
  },
  {
    path: ROUTES.ASSISTANT_DU_LIST_OF_DOCS_TO_CHANGE,
    element: <DU_ListOfDocsToChange />,
  },
  {
    path: ROUTES.ASSISTANT_DU_MULTI_STEP_FORM,
    element: <DU_MultiStepForm />,
  },
  {
    path: ROUTES.ASSISTANT_DU_GENERATING_DOCS,
    element: <LoadingNewDocs />,
  },
  {
    path: ROUTES.ASSISTANT_DU_UPDATE_DOCS,
    element: <DU_UpdateDocs />,
  },
  {
    path: ROUTES.TERMINAL,
    element: <TerminalPage />,
  },
  {
    path: ROUTES.USER_SETTINGS,
    element: <UserSettings />,
  },
  {
    path: ROUTES.ADMIN_TASK_DEFINITIONS,
    element: <AdminTaskDefinitions />,
  },
  {
    path: ROUTES.ADMIN_ROADMAP_CARDS,
    element: <AdminRoadmapCards />,
  },
  {
    path: ROUTES.QUALITY_EVENTS,
    element: <QualityEvents />,
  },
  {
    path: ROUTES.QUALITY_EVENT_OVERVIEW,
    element: <QualityEvents />,
  },
  {
    path: ROUTES.QUALITY_EVENT_RECORD,
    element: <QualityEventRecordPage />,
  },
  {
    path: ROUTES.TRAININGS,
    element: <TrainingPrograms />,
  },
  {
    path: ROUTES.TRAINING_ASSIGN,
    element: <TrainingAssignment />,
  },
  {
    path: ROUTES.TRAINING_DETAIL,
    element: <TrainingDetail />,
  },
  {
    path: ROUTES.TRAINING_EDIT,
    element: <TrainingEdit />,
  },
  {
    path: ROUTES.TRAINING_QUIZ_EDIT,
    element: <TrainingQuizEdit />,
  },
];

export const SaveLocationWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const currentPath = `${location.pathname}${location.search}`;
      sessionStorage.setItem("returnTo", currentPath);
    }
  }, [location, isAuthenticated, isLoading]);

  return <>{children}</>;
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Auth0Provider
        domain={config.auth.issuerBaseUrl}
        clientId={config.auth.clientId}
        audience={config.auth.audience}
        redirectUri={window.location.origin + ROUTES.CALLBACK}
        cacheLocation="localstorage"
        useRefreshTokens={true}
      >
        <Outlet />
      </Auth0Provider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.LOGIN} replace />,
      },
      {
        path: ROUTES.CALLBACK,
        element: <NavigateToHomeOrReturnTo />,
      },
      {
        path: ROUTES.LOGIN,
        element: <AuthPage type="login" />,
      },
      {
        path: ROUTES.SIGNUP,
        element: <AuthPage type="signup" />,
      },
      {
        path: ROUTES.HELP_CENTER,
        element: <HelpCenter />,
      },
      { path: ROUTES.HELP_CENTER_ARTICLE, element: <HelpCenterArticle /> },
      {
        path: ROUTES.DEACTIVATED_USER,
        element: <DeactivatedUserError />,
      },
      {
        path: ROUTES.ECFR_PIN_RESET,
        element: <ProtectedRoute component={PinReset} />,
      },
      {
        path: ROUTES.OAUTH_CALLBACK,
        element: <OAuthCallback />,
      },
      {
        path: `/${ROUTE_SLUGS.ORG_ID}`,
        element: (
          <SaveLocationWrapper>
            <ProtectedRoute component={MainLayout} />
          </SaveLocationWrapper>
        ),
        children: [
          {
            path: "",
            element: <Outlet />,
            children: GENERAL_ROUTES.map((route) => ({
              path: route.path,
              element: route.element,
              loader: route.loader || undefined,
            })),
          },
        ],
      },
    ],
  },
]);
